/** The row-and-bar drawing behind `HBarList` and `Funnel` (ADR-0212, P7). Internal: pages use the two
 * wrappers, which say what a row is.
 *
 * A row is a label, a bar and the figure as the caller prints it. **Bar length is geometry only**:
 * `value` against the largest value in the list, so rows compare; the printed `text` is the caller's
 * and nothing here sums or rounds (rule 5). A row with no `value` draws a hatched, dashed outline
 * with no length - "not assessed" is neither a short bar nor an absent one (rule 7).
 *
 * Behaviour shared by both: each row is one focusable mark with its full sentence as its name (a
 * roving tab stop, Up/Down/Home/End), hover or focus shows a tooltip and dims the other rows, and a
 * row with a `receipt` opens it on click or Enter when the caller passes `onOpenReceipt`. The bars
 * grow once from the left on mount and are still under reduced motion. The container is a `group`
 * named by `caption`; `ChartTable` carries the same rows as text, and inside a `ChartCard` the Table
 * button shows that table in place of the bars (`tableRows` lets a list put rows in the table that it
 * chose not to draw). Rows are 22px apart with 12px labels.
 */

import { motion, useReducedMotion } from "motion/react";
import { useRef, useState } from "react";
import type { ReactNode } from "react";
import type { KeyboardEvent } from "react";

import chart from "./Chart.module.css";
import { ChartTable } from "./ChartTable";
import { ChartTooltip } from "./ChartTooltip";
import { useChartAsTable } from "./chartView";
import styles from "./BarRows.module.css";
import { colourOf } from "./chartTokens";
import type { ChartColour } from "./chartTokens";
import { canOpen, receiptProps } from "./receipt";
import type { OpenReceipt } from "./receipt";
import { useChartTip } from "./useChartTip";

export interface BarRow {
  key: string;
  label: string;
  /** A second line under the label. */
  sub?: string;
  /** Drives the bar's length against the largest value in the list. `null` draws no bar. */
  value: number | null;
  /** The figure as printed (the caller's formatter). */
  text: string;
  /** Drawn with a hatched pattern and dashed outline, and no length. */
  hatched?: boolean;
  colour: ChartColour;
  /** Extra words for the tooltip. */
  note?: string;
  /** Printed between this row and the next (a funnel's step), already formatted. */
  between?: string;
  receipt?: string;
  /** The bar as parts of one whole, each in its own colour (a site's Scope 1 and Scope 2). The bar's
   * length is still `value` against the largest; a part's width is its `value` against the others'. */
  segments?: readonly BarSegment[];
}

/** One part of a stacked bar. `value` is geometry only; `text` is the part as printed. */
export interface BarSegment {
  key: string;
  label: string;
  colour: ChartColour;
  value: number;
  text: string;
}

export function BarRows({
  rows,
  caption,
  head,
  align = "start",
  testId,
  onOpenReceipt,
  hatchedWord = "Not assessed",
  tableRows,
  footer,
}: {
  rows: readonly BarRow[];
  caption: string;
  /** Table headings: the row name, then the figure with its unit. */
  head: readonly [string, string];
  align?: "start" | "centre";
  testId: string;
  onOpenReceipt?: OpenReceipt;
  /** The words a hatched row's tooltip and table carry. */
  hatchedWord?: string;
  /** Rows for the text alternative when it should hold more than the drawing (default: `rows`). */
  tableRows?: readonly BarRow[];
  /** One quiet line under the bars: what was left out, with its link. */
  footer?: ReactNode;
}) {
  const asTable = useChartAsTable();
  const wrap = useRef<HTMLDivElement>(null);
  const refs = useRef(new Map<number, HTMLElement>());
  const reduce = useReducedMotion();
  const { tip, show, hide } = useChartTip(wrap);
  const [focus, setFocus] = useState(0);
  const [hot, setHot] = useState<number | null>(null);

  const largest = Math.max(0, ...rows.map((r) => (r.hatched || r.value === null ? 0 : r.value)));
  const words = (row: BarRow) => (row.hatched ? hatchedWord : row.text);
  const parts = (row: BarRow) => (row.segments ?? []).map((part) => `${part.label}: ${part.text}`);
  const sentence = (row: BarRow) =>
    `${row.label}${row.sub ? `, ${row.sub}` : ""}: ${words(row)}${parts(row).length ? ` (${parts(row).join(", ")})` : ""}${row.note ? `. ${row.note}` : ""}${row.between ? `. ${row.between}` : ""}${canOpen(row.receipt, onOpenReceipt) ? ". Opens the receipt" : ""}`;

  const point = (index: number) => {
    const row = rows[index];
    if (!row) return;
    setHot(index);
    show(refs.current.get(index), row.label, [words(row), ...parts(row), ...(row.note ? [row.note] : [])]);
  };
  const leave = () => {
    setHot(null);
    hide();
  };
  const move = (index: number) => {
    const next = Math.max(0, Math.min(rows.length - 1, index));
    setFocus(next);
    refs.current.get(next)?.focus();
  };
  const onKey = (event: KeyboardEvent<HTMLElement>, index: number) => {
    if (event.key === "ArrowDown") move(index + 1);
    else if (event.key === "ArrowUp") move(index - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(rows.length - 1);
    else if (event.key === "Escape") leave();
    else return;
    event.preventDefault();
  };

  const table = (
    <ChartTable
      visible={asTable}
      caption={caption}
      head={[head[0], head[1]]}
      rows={(tableRows ?? rows).map((row) => [
        row.label,
        [words(row), parts(row).join(", "), row.note, row.between].filter(Boolean).join(". "),
      ])}
    />
  );
  if (asTable) {
    return (
      <div ref={wrap} className={`${chart.chart} ${styles.wrap}`} data-testid={testId}>
        {table}
      </div>
    );
  }

  // A bar of parts needs a key to its colours (G-Wapprover-20): the first appearance of each part, in order.
  const keyed: BarSegment[] = [];
  for (const row of rows)
    for (const part of row.segments ?? []) if (!keyed.some((known) => known.key === part.key)) keyed.push(part);

  return (
    <div ref={wrap} className={`${chart.chart} ${styles.wrap}`} data-testid={testId}>
      {keyed.length > 0 ? (
        <ul className={chart.legend} data-testid="bar-legend" aria-label="Colours">
          {keyed.map((part) => (
            <li key={part.key} className={chart.legendItem}>
              <span className={chart.swatch} style={{ background: colourOf(part.colour) }} aria-hidden="true" />
              {part.label}
            </li>
          ))}
        </ul>
      ) : null}
      <div
        className={styles.rows}
        role={rows.some((row) => canOpen(row.receipt, onOpenReceipt)) ? "toolbar" : "group"}
        aria-orientation={rows.some((row) => canOpen(row.receipt, onOpenReceipt)) ? "vertical" : undefined}
        aria-label={caption}
      >
        {rows.map((row, index) => {
          const open = receiptProps(row.receipt, row.key, onOpenReceipt);
          const fraction = row.hatched || row.value === null || largest <= 0 ? 0 : Math.max(0, row.value) / largest;
          return (
            <div key={row.key} className={styles.item}>
              <div
                ref={(node) => {
                  if (node) refs.current.set(index, node);
                  else refs.current.delete(index);
                }}
                role={open.onClick ? "button" : "img"}
                tabIndex={focus === index ? 0 : -1}
                aria-label={sentence(row)}
                className={`${styles.row} ${hot !== null && hot !== index ? styles.dim : ""} ${open.onClick ? styles.openable : ""}`}
                data-testid="bar-row"
                data-state={row.hatched ? "hatched" : "drawn"}
                data-receipt={open["data-receipt"]}
                onClick={open.onClick}
                onKeyDown={(event) => {
                  onKey(event, index);
                  if (!event.defaultPrevented) open.onKeyDown?.(event);
                }}
                onFocus={() => {
                  setFocus(index);
                  point(index);
                }}
                onBlur={leave}
                onPointerEnter={() => {
                  point(index);
                }}
                onPointerLeave={leave}
              >
                <span className={styles.name} aria-hidden="true">
                  <span className={styles.nameText}>{row.label}</span>
                  {row.sub ? <span className={styles.sub}>{row.sub}</span> : null}
                </span>
                <span
                  className={`${styles.track} ${align === "centre" ? styles.centre : ""}`}
                  aria-hidden="true"
                >
                  {row.hatched ? (
                    <span className={styles.hatch} data-testid="bar-hatch" />
                  ) : row.segments && row.segments.length > 0 ? (
                    <motion.span
                      className={styles.stack}
                      style={{
                        width: `${(fraction * 100).toFixed(2)}%`,
                        transformOrigin: align === "centre" ? "center" : "left",
                      }}
                      data-testid="bar-fill"
                      initial={reduce ? false : { scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={reduce ? { duration: 0 } : { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.04 }}
                    >
                      {row.segments.map((part) => (
                        <span
                          key={part.key}
                          className={styles.part}
                          data-testid="bar-segment"
                          data-part={part.key}
                          style={{ flexGrow: Math.max(0, part.value), background: colourOf(part.colour) }}
                        />
                      ))}
                    </motion.span>
                  ) : (
                    <motion.span
                      className={styles.fill}
                      style={{
                        width: `${(fraction * 100).toFixed(2)}%`,
                        background: colourOf(row.colour),
                        transformOrigin: align === "centre" ? "center" : "left",
                      }}
                      data-testid="bar-fill"
                      initial={reduce ? false : { scaleX: 0 }}
                      animate={{ scaleX: 1 }}
                      transition={reduce ? { duration: 0 } : { duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: index * 0.04 }}
                    />
                  )}
                </span>
                <span className={`${styles.value} num`} aria-hidden="true">
                  {row.hatched ? <span className={styles.words}>{hatchedWord}</span> : row.text}
                </span>
              </div>
              {row.between ? (
                <span className={styles.between} aria-hidden="true">
                  {row.between}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
      <ChartTooltip anchor={tip?.anchor ?? null}>
        <b>{tip?.title}</b>
        {tip?.lines.map((line) => (
          <span key={line} className={styles.tipLine}>
            {line}
          </span>
        ))}
      </ChartTooltip>
      {footer ? <div className={styles.footer}>{footer}</div> : null}
      {table}
    </div>
  );
}
